import {formatDate} from '@angular/common';
import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardCompanyTimeline,
  AdminDashboardFlagRun,
  AdminDashboardPoint
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardOptionalPoint} from '@features/admin-panel/models/admin-dashboard-c360-engagement.models';
import {
  adbAlpha,
  adbCategoryAxis,
  adbChartTheme,
  adbGrid,
  adbRedrawOnThemeChange,
  adbTooltip,
  adbValueAxis,
  adbZoom
} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const c360TimelineCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** One flag run as a band behind the health line: indexes into the health points (ITP-E-155-03). */
export interface C360FlagBand {
  kind: string;
  label: string;
  fromIndex: number;
  toIndex: number;
}

/**
 * Maps each flag run onto the health chart's x axis: from the first health day on or after the run's `from` to the
 * last health day on or before its `to`. A run with no health day inside it is dropped.
 */
export function c360FlagBands(health: AdminDashboardPoint[], runs: AdminDashboardFlagRun[]): C360FlagBand[] {
  const days = (health ?? []).map(point => Date.parse(point.date));
  const bands: C360FlagBand[] = [];
  for (const run of runs ?? []) {
    const from = Date.parse(run.from);
    const to = Date.parse(run.to);
    const fromIndex = days.findIndex(day => day >= from);
    let toIndex = -1;
    days.forEach((day, i) => {
      if (day <= to) {
        toIndex = i;
      }
    });
    if (fromIndex >= 0 && toIndex >= fromIndex) {
      bands.push({kind: run.kind, label: run.label, fromIndex, toIndex});
    }
  }
  return bands;
}

/**
 * Maps each flag run onto the 52-week chart (ITP-E-166-04): week i covers the 7 days ending on `weeks[i].date`. A run
 * starts on the first week ending on or after its `from` and ends on the first week ending on or after its `to` (the
 * last week when `to` is later). A run that ended before the first week began is dropped.
 */
export function c360WeeklyFlagBands(weeks: AdminDashboardOptionalPoint[], runs: AdminDashboardFlagRun[]): C360FlagBand[] {
  const ends = (weeks ?? []).map(week => Date.parse(week.date));
  const bands: C360FlagBand[] = [];
  if (!ends.length) {
    return bands;
  }
  const firstDay = ends[0] - 6 * 86400000;
  for (const run of runs ?? []) {
    const from = Date.parse(run.from);
    const to = Date.parse(run.to);
    const fromIndex = ends.findIndex(end => end >= from);
    if (fromIndex < 0 || to < firstDay) {
      continue;
    }
    const toAt = ends.findIndex(end => end >= to);
    const toIndex = Math.max(fromIndex, toAt < 0 ? ends.length - 1 : toAt);
    bands.push({kind: run.kind, label: run.label, fromIndex, toIndex});
  }
  return bands;
}

/** The tier a score falls in (InsightRules.TierOf: critical < 40 <= warning < 65 <= watch < 80 <= healthy). */
export function c360TierOf(score: number): string {
  return score < 40 ? 'Critical' : score < 65 ? 'Warning' : score < 80 ? 'Watch' : 'Healthy';
}

/** What became of a flag run (AdminDashboardCompanyTimelineQueryHandler) as the pill says it (M/company360.ts OUTCOME_LABEL). */
const OUTCOME: {[outcome: string]: {label: string; tone: string}} = {
  open: {label: 'Open', tone: 'amber'},
  snoozed: {label: 'Snoozed', tone: 'blue'},
  handled: {label: 'Handled', tone: 'green'},
  cleared: {label: 'Cleared on its own', tone: 'neutral'}
};

/**
 * Company 360 "Health and flag timeline" (OCA-E-159 M/charts.ts:677-753): the daily health score as a dark line with
 * points, dashed tier lines, every flag run as a band behind it, zoom; and every flag run with its outcome.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-c360-timeline',
  templateUrl: './admin-dashboard-c360-timeline.component.html',
  styleUrls: ['./admin-dashboard-c360-timeline.component.scss']
})
export class AdminDashboardC360TimelineComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() companyId: string | null = null;
  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;
  /** The daily health scores each time they load, for the page's Health score tile (ITP-E-155-02). */
  @Output() healthLoaded = new EventEmitter<AdminDashboardPoint[]>();

  health: AdminDashboardPoint[] = [];
  runs: AdminDashboardFlagRun[] = [];
  /** The 52 weekly scores (ITP-E-166-03 `Weekly52`); empty from an older API, which keeps the 90-day daily chart. */
  weekly: AdminDashboardOptionalPoint[] = [];

  /** True when the chart draws the 52 weekly scores instead of the 90 daily ones. */
  get isWeekly(): boolean {
    return this.weekly.length > 0;
  }

  /** What the chart draws: the weekly scores (gaps as null) or the daily health. */
  get chartPoints(): AdminDashboardOptionalPoint[] {
    return this.isWeekly ? this.weekly : this.health;
  }

  /** At least one drawn point has a score; otherwise the card shows its empty state. */
  get hasScores(): boolean {
    return this.chartPoints.some(point => point.value !== null && point.value !== undefined);
  }

  /** The flag runs as bands on whichever axis the chart draws. */
  get bands(): C360FlagBand[] {
    return this.isWeekly ? c360WeeklyFlagBands(this.weekly, this.runs) : c360FlagBands(this.health, this.runs);
  }
  /** False until the first response, so the empty state never flashes. */
  loaded = false;

  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId']) {
      this.load();
    }
  }

  ngAfterViewInit(): void {
    this.chart = c360TimelineCharts.init(this.chartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.render();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
  }

  /**
   * Daily: "<b>1 Oct 2026</b><br/>Health 38 · Critical". Weekly (ITP-E-166-04, M/charts.ts:705-711): "<b>Week of 2 Oct</b>
   * <br/>Health 38 · Critical", or "<b>Week of 2 Oct</b><br/>No health score this week" for a gap. Both add
   * "<br/>Flagged: …" naming every run that covers the point.
   */
  tooltipText(index: number): string {
    const point = this.chartPoints[index];
    if (!point) {
      return '';
    }
    const flagged = this.bands
      .filter(band => index >= band.fromIndex && index <= band.toIndex)
      .map(band => band.label);
    const title = this.isWeekly
      ? `<b>Week of ${formatDate(point.date, 'd MMM', 'en-US', 'UTC')}</b>`
      : `<b>${formatDate(point.date, 'd MMM y', 'en-US', 'UTC')}</b>`;
    if (point.value === null || point.value === undefined) {
      return `${title}<br/>No health score this week`;
    }
    return `${title}<br/>Health ${point.value} · ${c360TierOf(point.value)}`
      + (flagged.length ? `<br/>Flagged: ${flagged.join(', ')}` : '');
  }

  /** "Open", "Snoozed", "Handled", "Cleared on its own"; an unknown outcome as it came. */
  outcomeLabel(outcome: string): string {
    return OUTCOME[outcome]?.label ?? outcome;
  }

  /** The outcome pill's tone (`data-tone`): open amber, snoozed blue, handled green, anything else neutral. */
  outcomeTone(outcome: string): string {
    return OUTCOME[outcome]?.tone ?? 'neutral';
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    this.loaded = false;
    if (!this.companyId) {
      return;
    }
    this.loadSubscription = this.dataService.getCompanyTimeline(this.companyId).pipe(
      catchError(() => of({Health: [], Flags: [], Weekly52: []} as AdminDashboardCompanyTimeline))
    ).subscribe(timeline => {
      this.health = timeline?.Health ?? [];
      this.runs = timeline?.Flags ?? [];
      this.weekly = timeline?.Weekly52 ?? [];
      this.loaded = true;
      this.healthLoaded.emit(this.health);
      this.render();
    });
  }

  private render(): void {
    if (!this.chart) {
      return;
    }
    const t = adbChartTheme(this.chartContainer.nativeElement);
    const points = this.chartPoints;
    const labels = points.map(point => formatDate(point.date, 'd MMM', 'en-US', 'UTC'));
    const bandColor = (kind: string): string =>
      kind === 'renewal' ? adbAlpha(t.red, 0.12)
        : kind === 'dark' ? adbAlpha(t.amber, 0.16)
          : kind === 'usage' ? adbAlpha(t.orange, 0.16)
            : kind === 'spike' ? adbAlpha(t.blue, 0.12)
              : adbAlpha(t.ink3, 0.12);
    const option: echarts.EChartsOption = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid({top: 16, bottom: 52}),
      xAxis: adbCategoryAxis(t, labels, {boundaryGap: false}),
      yAxis: adbValueAxis(t, value => String(value), {min: 0, max: 100, interval: 20}),
      dataZoom: adbZoom(t),
      tooltip: adbTooltip(t, {
        formatter: (params: any) => this.tooltipText((Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0)
      }),
      series: [{
        type: 'line',
        name: 'Health',
        data: points.map(point => point.value ?? null),
        connectNulls: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {width: 2, color: t.ink},
        itemStyle: {color: t.ink},
        markLine: {
          silent: true,
          symbol: 'none',
          label: {color: t.ink2, fontSize: 10, position: 'insideStartTop'},
          data: [
            {yAxis: 40, lineStyle: {color: t.red, type: 'dashed', width: 1}, label: {formatter: 'Critical below 40'}},
            {yAxis: 65, lineStyle: {color: t.amber, type: 'dashed', width: 1}, label: {formatter: 'Warning below 65'}},
            {yAxis: 80, lineStyle: {color: t.blue, type: 'dashed', width: 1}, label: {formatter: 'Watch below 80'}}
          ]
        },
        markArea: {
          silent: true,
          data: this.bands.map(band => [
            {xAxis: band.fromIndex, itemStyle: {color: bandColor(band.kind)}},
            {xAxis: band.toIndex}
          ] as [{xAxis: number; itemStyle: {color: string}}, {xAxis: number}])
        }
      }]
    };
    this.chart.setOption(option, true);
  }
}
